import React, { useState } from 'react'
import { DataTable, DataTableSortEvent } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { Button } from 'primereact/button'
import { Toolbar } from 'primereact/toolbar'
import { Dialog } from 'primereact/dialog'
import { InputText } from 'primereact/inputtext'
import { Dropdown } from 'primereact/dropdown'
import { IconField } from 'primereact/iconfield'
import { InputIcon } from 'primereact/inputicon'
import { Skeleton } from 'primereact/skeleton'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  listProductsRequest,
  createProductRequest,
  patchProductRequest,
  deleteProductRequest,
} from '../apis/calyxSaaSDashboardAPI'
import { useApiErrorHandler } from '../hooks/useApiErrorHandler'
import { useToast } from '../hooks/useToast'
import { formatUTCTime } from '../utils/format'
import type { ProductResBodyDto, CreateProductReqBodyDto, PatchProductReqBodyDto } from '../apis/hey-api'

type ProductType = CreateProductReqBodyDto['type']

const PRODUCT_TYPES: readonly ProductType[] = [
  'AI_EYE_OTP',
  'AI_EYE_LEASING',
  'AI_EYE_SERVICE',
  'Y_SERIES_OTP',
  'Y_SERIES_LEASING',
  'Y_SERIES_SERVICE',
  'WATER_METER_OTP',
  'WATER_METER_LEASING',
  'WATER_METER_SERVICE',
  'OTHER',
]

interface ProductFormData {
  id?: string
  name: string
  type: ProductType | null
  memo: string | null
  createdTime?: string
  updatedTime?: string
  _revision?: number
}

const emptyProduct: ProductFormData = {
  name: '',
  type: null,
  memo: null,
}

const LIST_PRODUCTS_QUERY_KEY = ['list', 'products'] as const

export const CrudProducts = () => {
  const queryClient = useQueryClient()
  const toast = useToast()
  const handleError = useApiErrorHandler()

  const [createOrEditDialogVisible, setCreateOrEditDialogVisible] = useState(false)
  const [isCreateMode, setIsCreateMode] = useState(false)
  const [detailDialogVisible, setDetailDialogVisible] = useState(false)
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false)
  const [focusedProduct, setFocusedProduct] = useState<ProductFormData>(emptyProduct)
  const [globalFilter, setGlobalFilter] = useState<string>('')
  const [sortField, setSortField] = useState<string>('name')
  const [sortOrder, setSortOrder] = useState<DataTableSortEvent['sortOrder']>(1)

  // --- Queries ---

  const listProductsQuery = useQuery<ProductResBodyDto[]>(
    LIST_PRODUCTS_QUERY_KEY,
    () => listProductsRequest(),
    { onError: handleError },
  )

  // --- Mutations ---

  const createProductMutation = useMutation(
    ({ body }: { body: CreateProductReqBodyDto }) => createProductRequest(body),
    {
      onSuccess: (newProduct) => {
        queryClient.setQueryData<ProductResBodyDto[]>(LIST_PRODUCTS_QUERY_KEY, (products) => [...(products ?? []), newProduct])

        toast.current?.show({ severity: 'success', summary: 'Success', detail: 'New product has been saved', life: 3000 })

        hideCreateOrEditDialog()
      },
      onError: handleError,
    },
  )

  const patchProductMutation = useMutation(
    ({ productId, body }: { productId: string; body: PatchProductReqBodyDto }) => patchProductRequest(productId, body),
    {
      onSuccess: (updatedProduct, { productId }) => {
        queryClient.setQueryData<ProductResBodyDto[]>(LIST_PRODUCTS_QUERY_KEY, (products) =>
          products?.map((product) => (product.id === productId ? updatedProduct : product))
        )

        toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Product has been updated', life: 3000 })

        hideCreateOrEditDialog()
      },
      onError: handleError,
    },
  )

  const deleteProductMutation = useMutation(
    ({ productId }: { productId: string }) => deleteProductRequest(productId),
    {
      onSuccess: (_, { productId }) => {
        queryClient.setQueryData<ProductResBodyDto[]>(LIST_PRODUCTS_QUERY_KEY, (products) =>
          products?.filter((product) => product.id !== productId)
        )

        toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Product has been deleted', life: 3000 })

        hideDeleteDialog()
      },
      onError: handleError,
    },
  )

  // --- Dialog helpers ---

  const openCreateDialog = () => {
    setIsCreateMode(true)
    setFocusedProduct(emptyProduct)
    setCreateOrEditDialogVisible(true)
  }

  const openEditDialog = (product: ProductResBodyDto) => {
    setIsCreateMode(false)
    setFocusedProduct(product)
    setCreateOrEditDialogVisible(true)
  }

  const hideCreateOrEditDialog = () => {
    setCreateOrEditDialogVisible(false)
  }

  const openDetailDialog = (product: ProductResBodyDto) => {
    setFocusedProduct(product)
    setDetailDialogVisible(true)
  }

  const hideDetailDialog = () => {
    setDetailDialogVisible(false)
  }

  const openDeleteDialog = (product: ProductResBodyDto) => {
    setFocusedProduct(product)
    setDeleteDialogVisible(true)
  }

  const hideDeleteDialog = () => {
    setDeleteDialogVisible(false)
  }

  // --- Actions ---

  const saveProduct = () => {
    if (!focusedProduct.name || !focusedProduct.type) {
      toast.current?.show({ severity: 'error', summary: 'Validation Error', detail: 'Please fill in required fields before apply.', sticky: true })
      return
    }

    if (isCreateMode) {
      createProductMutation.mutate({
        body: {
          name: focusedProduct.name,
          type: focusedProduct.type,
          memo: focusedProduct.memo || null,
        },
      })
    } else {
      patchProductMutation.mutate({
        productId: focusedProduct.id!,
        body: {
          name: focusedProduct.name,
          type: focusedProduct.type,
          memo: focusedProduct.memo || null,
          _revision: focusedProduct._revision,
        },
      })
    }
  }

  const confirmDelete = () => {
    deleteProductMutation.mutate({ productId: focusedProduct.id! })
  }

  const onInputChange = (event: { target: { value: string } }, field: keyof ProductFormData) => {
    setFocusedProduct((prev) => ({ ...prev, [field]: event.target.value }))
  }

  // --- Templates ---

  const leftToolbarTemplate = () => (
    <div className="my-2">
      <Button label="New" icon="pi pi-plus" className="p-button-success mr-2" onClick={openCreateDialog} />
    </div>
  )

  const nameBodyTemplate = (rowData: ProductResBodyDto) => (
    <>
      <span className="p-column-title">Name</span>
      {rowData.name}
    </>
  )

  const typeBodyTemplate = (rowData: ProductResBodyDto) => (
    <>
      <span className="p-column-title">Type</span>
      {rowData.type}
    </>
  )

  const createdTimeBodyTemplate = (rowData: ProductResBodyDto) => (
    <>
      <span className="p-column-title">Created Time</span>
      {formatUTCTime(new Date(rowData.createdTime))}
    </>
  )

  const memoBodyTemplate = (rowData: ProductResBodyDto) => (
    <>
      <span className="p-column-title">Memo</span>
      {rowData.memo}
    </>
  )

  const actionBodyTemplate = (rowData: ProductResBodyDto) => (
    <div className="actions flex flex-wrap gap-2">
      <Button tooltip="detail" icon="pi pi-info-circle" className="p-button-rounded p-button-info" onClick={() => openDetailDialog(rowData)} />
      <Button tooltip="edit" icon="pi pi-pencil" className="p-button-rounded p-button-success" onClick={() => openEditDialog(rowData)} />
      <Button tooltip="delete" icon="pi pi-trash" className="p-button-rounded p-button-danger" onClick={() => openDeleteDialog(rowData)} />
    </div>
  )

  const header = (
    <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center">
      <h5 className="m-0">Manage Products</h5>
      <IconField iconPosition="left" className="block mt-2 md:mt-0">
        <InputIcon className="pi pi-search" />
        <InputText type="search" onChange={(event) => setGlobalFilter(event.target.value)} placeholder="Search..." />
      </IconField>
    </div>
  )

  const isMutating = createProductMutation.isLoading || patchProductMutation.isLoading

  const createOrEditDialogFooter = (
    <>
      <Button label="Cancel" icon="pi pi-times" className="p-button-text" onClick={hideCreateOrEditDialog} disabled={isMutating} />
      <Button label="Save" icon="pi pi-check" className="p-button-text" onClick={saveProduct} disabled={isMutating} />
    </>
  )

  const deleteDialogFooter = (
    <>
      <Button label="No" icon="pi pi-times" className="p-button-text" onClick={hideDeleteDialog} disabled={deleteProductMutation.isLoading} />
      <Button label="Yes" icon="pi pi-check" className="p-button-text" onClick={confirmDelete} disabled={deleteProductMutation.isLoading} />
    </>
  )

  // --- Render ---

  return (
    <div className="grid crud-demo">
      <div className="col-12">
        <div className="card">
          <Toolbar className="mb-4" left={leftToolbarTemplate} />

          {listProductsQuery.isLoading ? (
            <DataTable value={Array.from({ length: 10 }) as Record<string, unknown>[]} className="p-datatable-striped" header={header}>
              <Column header="Name" style={{ width: '20%', minWidth: '10rem' }} body={<Skeleton />} />
              <Column header="Type" style={{ width: '20%', minWidth: '10rem' }} body={<Skeleton />} />
              <Column header="Memo" style={{ width: '20%', minWidth: '8rem' }} body={<Skeleton />} />
              <Column header="Created Time" style={{ width: '20%', minWidth: '10rem' }} body={<Skeleton />} />
              <Column header="Action" style={{ width: '20%', minWidth: '12rem' }} body={<Skeleton />} />
            </DataTable>
          ) : (
            <DataTable
              value={listProductsQuery.data}
              dataKey="id"
              paginator
              rows={10}
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
              className="datatable-responsive"
              paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
              currentPageReportTemplate="Showing {first} to {last} of {totalRecords} products"
              sortField={sortField}
              sortOrder={sortOrder}
              onSort={(event) => { setSortField(event.sortField); setSortOrder(event.sortOrder) }}
              globalFilter={globalFilter}
              emptyMessage="No products found."
              header={header}
              responsiveLayout="scroll"
            >
              <Column header="Name" field="name" sortable body={nameBodyTemplate} headerStyle={{ width: '20%', minWidth: '10rem' }} />
              <Column header="Type" field="type" sortable body={typeBodyTemplate} headerStyle={{ width: '20%', minWidth: '10rem' }} />
              <Column header="Memo" field="memo" body={memoBodyTemplate} headerStyle={{ width: '20%', minWidth: '8rem' }} />
              <Column header="Created Time" field="createdTime" sortable body={createdTimeBodyTemplate} headerStyle={{ width: '20%', minWidth: '10rem' }} />
              <Column header="Action" body={actionBodyTemplate} headerStyle={{ width: '20%', minWidth: '12rem' }} />
            </DataTable>
          )}

          {/* Create / Edit Dialog */}
          <Dialog
            blockScroll
            visible={createOrEditDialogVisible}
            style={{ width: '450px' }}
            header={isCreateMode ? 'Create new product' : 'Edit product'}
            modal
            className="p-fluid"
            footer={createOrEditDialogFooter}
            onHide={hideCreateOrEditDialog}
          >
            <div className="field">
              <label htmlFor="name">Name *</label>
              <InputText
                id="name"
                value={focusedProduct.name}
                onChange={(event) => onInputChange(event, 'name')}
                required
                autoFocus
                disabled={isMutating}
              />
              {!focusedProduct.name && <small className="p-invalid" style={{ color: 'red' }}>Name is required.</small>}
            </div>

            <div className="field">
              <label htmlFor="type">Type *</label>
              <Dropdown
                id="type"
                value={focusedProduct.type}
                options={PRODUCT_TYPES.map((type) => ({ label: type, value: type }))}
                onChange={(event) => onInputChange(event, 'type')}
                placeholder="Select a type"
                required
                disabled={isMutating}
              />
              {!focusedProduct.type && <small className="p-invalid" style={{ color: 'red' }}>Type is required.</small>}
            </div>

            <div className="field">
              <label htmlFor="memo">Memo</label>
              <InputText
                id="memo"
                value={focusedProduct.memo || ''}
                onChange={(event) => onInputChange(event, 'memo')}
                disabled={isMutating}
              />
            </div>
          </Dialog>

          {/* Detail Dialog */}
          <Dialog
            blockScroll
            visible={detailDialogVisible}
            style={{ width: '450px' }}
            header="Product Detail"
            modal
            onHide={hideDetailDialog}
          >
            <div className="field">
              <label style={{ color: 'gray', fontSize: '18px', marginRight: '10px' }}>ID: </label>
              <label>{focusedProduct.id}</label>
            </div>
            <div className="field">
              <label style={{ color: 'gray', fontSize: '18px', marginRight: '10px' }}>Name: </label>
              <label>{focusedProduct.name}</label>
            </div>
            <div className="field">
              <label style={{ color: 'gray', fontSize: '18px', marginRight: '10px' }}>Type: </label>
              <label>{focusedProduct.type}</label>
            </div>
            <div className="field">
              <label style={{ color: 'gray', fontSize: '18px', marginRight: '10px' }}>Memo: </label>
              <label>{focusedProduct.memo}</label>
            </div>
            <div className="field">
              <label style={{ color: 'gray', fontSize: '18px', marginRight: '10px' }}>Created Time: </label>
              <label>{focusedProduct.createdTime ? formatUTCTime(new Date(focusedProduct.createdTime)) : 'N/A'}</label>
            </div>
            <div className="field">
              <label style={{ color: 'gray', fontSize: '18px', marginRight: '10px' }}>Updated Time: </label>
              <label>{focusedProduct.updatedTime ? formatUTCTime(new Date(focusedProduct.updatedTime)) : 'N/A'}</label>
            </div>
          </Dialog>

          {/* Delete Confirmation Dialog */}
          <Dialog
            blockScroll
            visible={deleteDialogVisible}
            style={{ width: '450px' }}
            header="Confirm"
            modal
            footer={deleteDialogFooter}
            onHide={hideDeleteDialog}
          >
            <div className="flex align-items-center">
              <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem', color: 'var(--orange-500)' }} />
              <div>
                <span>Are you sure you want to delete this product?</span>
                <ul style={{ paddingLeft: '1.2rem', marginTop: '0.5rem', marginBottom: 0 }}>
                  <li><b>{focusedProduct.name}</b></li>
                </ul>
              </div>
            </div>
          </Dialog>
        </div>
      </div>
    </div>
  )
}
